/**
 * O que este dispositivo é — e o que ele consegue fazer.
 *
 * Duas perguntas parecidas que NÃO são a mesma, e confundi-las é o erro clássico
 * do "responsivo":
 *
 *   • É ESTREITO? (`MOBILE_QUERY`) — pergunta de LAYOUT. Abaixo de 900px as
 *     quatro colunas fixas do esqueleto (72 + 248 + conversa + 240) deixam de
 *     caber, e as laterais passam a gavetas. Uma janela de portátil encolhida a
 *     meio ecrã cai aqui, e ainda bem: também lá não cabem.
 *
 *   • É um TELEMÓVEL? (`PHONE_QUERY`) — estreito E sem apontador fino. Decide
 *     arrumações de ecrã pequeno (a barra de cima compacta, a coluna de
 *     servidores fora do escritório).
 *
 *   • É de TOQUE? (`TOUCH_QUERY`) — sem apontador fino, seja qual for a largura.
 *     É ESTA a pergunta do escritório 2D: sem teclado, anda-se com o joystick e
 *     toca-se nos avisos em vez da tecla E. O escritório existe em todo o lado
 *     desde 16/09/2026; antes desaparecia nos telemóveis.
 *
 * As duas cadeias estão escritas AQUI e mais nenhures no TypeScript. A folha de
 * estilo tem de as repetir à letra (o CSS não lê constantes), e é `media.test.ts`
 * que obriga as duas cópias a dizerem o mesmo — um `@media` a 900px com um
 * `matchMedia` a 768px dá uma gaveta aberta por cima de um layout de desktop, e
 * isso não dá erro nenhum: dá um ecrã partido em silêncio.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";

/** Abaixo disto as colunas laterais passam a gavetas. Igual ao `@media` do `styles.css`. */
export const MOBILE_QUERY = "(max-width: 900px)";

/**
 * Estreito E sem rato — um telemóvel a sério. `pointer: coarse` é o dedo: é o
 * que separa o telemóvel de uma janela de desktop encolhida, e é a diferença
 * que decide se o escritório 2D existe.
 */
export const PHONE_QUERY = "(max-width: 900px) and (pointer: coarse), (max-width: 900px) and (hover: none)";

/**
 * Ecrã de TOQUE, seja qual for a largura (telemóvel, tablet). É o que decide se
 * o escritório 2D mostra o joystick e os botões de toque — um tablet largo não
 * tem teclado na mesma.
 *
 * `hover: none` entra também, e não por enfeite: há browsers de telemóvel que se
 * declaram `pointer: fine` (o Chrome do emulador Android, que tem um rato ligado)
 * mas nenhum deles consegue "passar por cima" — e nenhum computador diz isso.
 */
export const TOUCH_QUERY = "(pointer: coarse), (hover: none)";

/**
 * A leitura crua. Fora do browser (jsdom sem `matchMedia`, um render no
 * servidor) a resposta é `false` — que é o desktop, o layout que sempre existiu.
 * Falhar para o lado que já funcionava é de propósito.
 */
function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

/** A mesma pergunta que `useIsMobile`, para quem não é um componente (a store). */
export function matchesMobile(): boolean {
  return matches(MOBILE_QUERY);
}

/** A mesma pergunta que `useIsPhone`, para quem não é um componente (a store). */
export function matchesPhone(): boolean {
  return matches(PHONE_QUERY);
}

/**
 * Uma media query como estado do React.
 *
 * `useSyncExternalStore` e não `useState` + `useEffect`: o valor lê-se no
 * momento do render, não um frame depois. Com o efeito, o primeiro paint de um
 * telemóvel era sempre o layout de desktop — as quatro colunas a espreitar meio
 * segundo antes de saltarem para as gavetas.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );
  // O terceiro argumento é o valor do servidor: sem `window`, desktop.
  return useSyncExternalStore(
    subscribe,
    () => matches(query),
    () => false,
  );
}

/** Ecrã estreito: as laterais são gavetas. */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/** Telemóvel: estreito e a dedo. */
export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/** Ecrã de toque — o escritório 2D põe o joystick. */
export function useIsTouch(): boolean {
  return useMediaQuery(TOUCH_QUERY);
}

/**
 * Publica a altura REAL visível em `--app-height`.
 *
 * `100vh` num telemóvel mente duas vezes: conta a barra de endereço que se
 * esconde ao rolar, e ignora por completo o teclado virtual. Com `100vh`, tocar
 * na caixa de escrever empurra o compositor para debaixo do teclado — escreve-se
 * às cegas, que é o defeito nº1 de um chat no telemóvel.
 *
 * `visualViewport.height` é a única medida que encolhe quando o teclado abre. O
 * `100dvh` do CSS fica como valor de arranque (e é o que os browsers sem
 * `visualViewport` usam), esta variável passa-lhe à frente assim que houver uma
 * medida verdadeira.
 *
 * Só o `resize` — o `scroll` do viewport visual dispara a rolar com o teclado
 * aberto sem que a altura mude, e reescrever a variável a cada evento fazia o
 * layout tremer.
 */
export function useViewportVar(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    const viewport = window.visualViewport;

    const apply = () => {
      const height = viewport?.height ?? window.innerHeight;
      if (height > 0) root.style.setProperty("--app-height", `${Math.round(height)}px`);
    };

    apply();
    viewport?.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    // Sem `visualViewport` (browsers antigos) o `resize` da janela é o que há.
    if (!viewport) window.addEventListener("resize", apply);

    return () => {
      viewport?.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      if (!viewport) window.removeEventListener("resize", apply);
      root.style.removeProperty("--app-height");
    };
  }, []);
}

/**
 * Este dispositivo consegue capturar o ecrã?
 *
 * A permissão de partilhar ecrã tem DOIS donos e ambos podem dizer não:
 *
 *   1. o servidor, via `capabilities.canShareScreen` — é uma decisão de cargo,
 *      "esta pessoa pode ou não pode";
 *   2. o próprio dispositivo — e aqui não há discussão nenhuma. O Safari do iOS
 *      não implementa `getDisplayMedia` (não existe API para capturar o ecrã de
 *      um iPhone a partir de uma página) e o Chrome do Android também não.
 *
 * Sem esta segunda pergunta, o botão de partilhar ecrã aparecia activo num
 * telemóvel, e tocar-lhe rebentava com um `TypeError` — que é a pior forma de
 * dizer "não dá": parece avaria da app. Perguntar antes transforma isso num
 * botão desligado com uma frase honesta.
 *
 * É uma prova de EXISTÊNCIA do método, não um palpite pelo user-agent: onde a
 * API existe, existe mesmo; onde não existe, a propriedade não está lá.
 */
export function screenShareSupported(): boolean {
  if (typeof navigator === "undefined") return false;
  const media: MediaDevices | undefined = navigator.mediaDevices;
  return typeof media?.getDisplayMedia === "function";
}
